// @givmo/board — the board invitation, where a director's emailed link lands (CBG-SEC2; mockup 1).
//
// The address is the one link format (CBG-FIX14): `{console}/board/director/redeem?token=…`, with a
// packet's version beside the token on a packet distribution's link. The link is an INVITATION now
// (D-CBG-16): it opens nothing by itself, and the seat is claimed into the director's own Givmo
// account by "match or prove" (D-CBG-17 decision 1).
//
// It answers the visitor's questions in order and asks for the act last (Jonathan's approved frames,
// D-CBG-18): is this legitimate (the Givmo header, an organization they know); who invited me
// ("<Organization> invited you to its board"); am I the right person (whose seat, and the address it
// went to, masked); then the act — the ways in, signed out, or one "Join the board", signed in.
//
// OPENING IT SPENDS NOTHING. The preview writes nothing, so a mail scanner that pre-fetches the link
// does no harm, and nothing here claims on load. Two ways to claim:
//   * a signed-in visitor taps "Join the board", with the account named above the button it binds;
//   * a visitor who signs in FROM this page joins at once (D-CBG-18 answer 3, "3) agree"): picking a
//     way in records the choice in this tab (`director/join-marker.ts`, never the address), and the
//     first signed-in render claims once.
//
// Every refusal is one the contract defines (`board-director-accounts.md` §3.3). An invitation that
// can't be used — unknown, expired, retired, spent, its seat removed or claimed by another account —
// gets ONE quiet page that names no organization, so it tells the link's holder nothing they didn't
// have. Its "Already joined? Sign in" line is on every refusal, so it is no oracle either.
//
// ANOTHER GIVMO ACCOUNT (CBG-SEC2-L4; D-CBG-23, Jonathan, 2026-10-07 17:13 PDT: a director whose Givmo
// account another organization's sign-in requirement locks makes another Givmo account for the board).
// When another organization's sign-in lock binds the signed-in account, the preview says so
// (`another_account_required`), and the landing offers no Join: it says why and offers "Create new
// account", leading, and "Use another Givmo account" (frame a1). The claim's own refusal of such an
// account, `board_another_account_required`, draws the same card. "Use another Givmo account" signs
// the account out and the signed-out invitation then hints at the other account above the ways in,
// which leave out the organization door, with "Create new account" under them (a2). At the door
// itself, a lock that refuses a way in gets Board's line in Account Security's notice (a0). Signed
// out, these words name no locking organization; signed in, a1 names it only where the account's own
// security read does. Jonathan approved every new line in copy round CBG-SEC2-L4-COPY1 (2026-10-12
// 13:49 PDT, "otherwise approved"), as written.
//
// AN INVITATION TO A COMMITTEE (CBG-FIX106, the FIX104 copy page's rows 4.7–4.10, approved
// 2026-10-17 17:42 PDT): before the preview says whose seat it is, the page is "Your invitation";
// the lock's line, and the refusal page's last line in a tab that saw the preview, say the
// committee; and "already has a seat" names the organization, true of a board's seat or a
// committee's, its button "Open your committees" when every seat the account holds is one. A
// refusal page that can't know whose invitation it was — a refused preview names nothing, so a tab
// that never read it — says the last line true of both, "Already joined? Sign in." (review-233's
// F1; approved as built in CBG-FIX111's copy round, its row 1.1, 2026-10-18 17:23 PDT).

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { firstErrorCode, firstErrorContext, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Link, boardPath, useNavigate, useSearch } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardBasePath, useBoardDirectorOperations } from '../../board-runtime';
import { directorRedeemSearch } from '../../director/director-access-link';
import { directorBoardIdentity, holdsOnlyCommitteeSeats } from '../../director/director-board';
import { useBoardDirectorHost } from '../../director/director-host';
import { decodeDirectorBoard, useDirectorMe } from '../../director/director-me';
import {
  anotherAccountOn,
  clearAnotherAccount,
  clearJoinIntent,
  clearSignInEnded,
  invitationCommitteesOn,
  markAnotherAccount,
  markCreateAnotherAccount,
  markInvitationCommittees,
  markJoinIntent,
  markSignInEnded,
  signInEndedOn,
  takeCreateAnotherAccount,
  takeJoinIntent,
} from '../../director/join-marker';
import {
  needsFactorSetup,
  readHoldsAuthenticator,
  useLatestStepUpHost,
} from '../../director/signing-gate';
import {
  DIRECTOR_REFUSALS,
  seatLinkUnavailableSentence,
  type BoardInvitationPreview,
  type DirectorBoard,
} from '../../director/director-wire';
import { DirectorSetupOfferPage } from './director-act';
import { DirectorShell } from './director-shell';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import {
  CodeStep,
  STEP_BODY as BODY,
  STEP_LINK as LINK,
  StepCard,
  UseAnotherAccount as UseAnotherAccountLink,
} from './director-steps';

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The preview's answer (`BoardInvitationPreviewOut`), read tolerantly: a malformed one is none. */
export function decodeInvitationPreview(data: unknown): BoardInvitationPreview | null {
  const row = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
  const seat = text(row?.seat_display_name);
  const masked = text(row?.masked_email_of_record);
  if (row === null || seat === null || masked === null) return null;
  const anotherAccount = row.another_account_required;
  return {
    organization_name: text(row.organization_name),
    seat_display_name: seat,
    masked_email_of_record: masked,
    claimed_by_you: row.claimed_by_you === true,
    // CBG-SEC2-L4: Board's verdict for the signed-in account, absent when it isn't known.
    another_account_required: typeof anotherAccount === 'boolean' ? anotherAccount : null,
    // CBG-YR6-OUTSIDE: a seat for someone who isn't on the board is invited to its committees, which
    // the preview names; any other scope, or none, is the board's, and names none.
    scope: text(row.scope) === 'committees' ? 'committees' : 'board',
    committee_names:
      text(row.scope) === 'committees' && Array.isArray(row.committee_names)
        ? row.committee_names.filter(
            (name): name is string => typeof name === 'string' && name.length > 0,
          )
        : [],
  };
}

/** The claim code's answer: where the code went, and whether it went at all. */
function decodeClaimCode(data: unknown): { emailSent: boolean; masked: string | null } {
  const row = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
  return { emailSent: row?.email_sent === true, masked: text(row?.masked_email_of_record) };
}

function refusalCode(error: unknown): string | null {
  return isGivmoError(error) ? (firstErrorCode(error) ?? null) : null;
}

/**
 * `linkUnavailable` (CBG-FIX161): Board couldn't link the seat just now, so the join didn't go
 * through; the step it was sent from stays, saying Board's sentence, and its act sends it again.
 */
type Step =
  | { kind: 'idle'; linkUnavailable?: string }
  | { kind: 'joining' }
  | { kind: 'code-ask'; masked: string; sending: boolean }
  /**
   * `failures` counts the codes refused since this one was sent: each clears the boxes and says so.
   * `throttled`: the code cap is reached. A code already sent still works, so the boxes stay and the
   * cap's line takes the resend link's place.
   */
  | {
      kind: 'code-step';
      masked: string;
      failures: number;
      sentAt: number;
      submitting: boolean;
      throttled?: boolean;
      linkUnavailable?: string;
    }
  | { kind: 'code-unsent'; reason: 'skip' | 'failed'; masked: string }
  | { kind: 'already-seated' }
  /** CBG-SEC2-L4: the claim refused this account for another Givmo account (frame a1). */
  | { kind: 'another-account' }
  | { kind: 'unavailable' }
  | { kind: 'failed' }
  | { kind: 'offering'; board: DirectorBoard | null };

export function DirectorInvitationPage() {
  const { token, packet } = directorRedeemSearch(useSearch());
  if (token === undefined) return <IncompleteLink />;
  return <Invitation token={token} packet={packet ?? null} />;
}

/** The address has no token: a mail app cut the link short. Nothing was asked of the backend. */
function IncompleteLink() {
  return (
    <DirectorShell title="This link is incomplete" testId="page-director-redeem">
      <p className={BODY} {...testIdProp('director-redeem-no-token')}>
        Part of the link seems to be missing. Some mail apps cut long links in half — try opening it
        again from the original message, or ask whoever sent it to send you another.
      </p>
    </DirectorShell>
  );
}

function Invitation({ token, packet }: { token: string; packet: string | null }) {
  const operations = useBoardDirectorOperations();
  const host = useBoardDirectorHost();
  const stepUpHost = useLatestStepUpHost();
  const navigate = useNavigate();
  const basePath = useBoardBasePath();
  const here = useRouterState({ select: (state) => state.location.href });
  const meQuery = useDirectorMe();
  const account = host.account;
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const autoClaimed = useRef(false);
  // The sign-in ended while this tab was joining (`signInEnded` below): read from the tab's marker,
  // because signing out remounts the page, and set here too for a host that doesn't remount it.
  const [endedWhileJoining, setEndedWhileJoining] = useState(() => signInEndedOn(token));
  // CBG-SEC2-L4: this tab chose "Use another Givmo account" and signed the locked account out (a2).
  const [lookingForAnother, setLookingForAnother] = useState(() => anotherAccountOn(token));

  // Signed in again: the lines have said their piece. Keyed on the fact, not the account object,
  // which a host may rebuild while the same account is still signed in.
  const signedIn = account !== null;
  useEffect(() => {
    if (!signedIn) return;
    clearSignInEnded();
    setEndedWhileJoining(false);
    clearAnotherAccount();
    setLookingForAnother(false);
  }, [signedIn]);

  const previewQuery = useQuery({
    // The answer depends on who asks (`claimed_by_you`), so the account is part of the key.
    queryKey: ['board-director-invitation', token, account?.email ?? null],
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorPreviewBoardInvitation({
        body: { invitation_token: token },
        throwOnError: true,
      });
      return decodeInvitationPreview(data);
    },
  });
  const preview = previewQuery.data ?? null;
  const masked = preview?.masked_email_of_record ?? '';

  // CBG-FIX106 (row 4.10): whose invitation this is — its committees, or 0 for the board's — kept in
  // this tab, so a refusal met here later, signed out, says the committee or the board (the page
  // remounts on a sign-out).
  useEffect(() => {
    if (preview === null) return;
    markInvitationCommittees(token, preview.committee_names.length);
  }, [preview, token]);

  const openBoard = async (board: DirectorBoard | null) => {
    // A packet distribution's invitation opens the version it was sent for; any other, the boards.
    if (packet !== null && board !== null) {
      await navigate({
        to: BOARD_DIRECTOR_PAGES.packet,
        params: { packetId: packet },
        search: { org: board.org_ref },
        replace: true,
      });
      return;
    }
    await navigate({ to: BOARD_DIRECTOR_PAGES.home, replace: true });
  };

  const joined = async (board: DirectorBoard | null) => {
    const name = board?.organization_name ?? preview?.organization_name ?? null;
    // CBG-YR6-OUTSIDE (not drawn): a committee-only seat joined its committees.
    const committees = preview?.committee_names ?? [];
    toast.success(
      committees.length > 0
        ? co.joined(name, committees)
        : name === null
          ? 'You’ve joined the board.'
          : `You’ve joined the ${name} board.`,
    );
    // Offered once, here: an account with neither factor meets Account Security's setup offer
    // before its board (derivation §1.5); one that has either goes straight on. Whether it holds an
    // authenticator is the host's answer, read now when the host reads the account on demand
    // (AS-WEB15), so "not read yet" never sends a no-factor director past the offer.
    const fresh = await meQuery.refetch();
    if (
      host.setupOffer !== null &&
      fresh.data?.signing.account_has_passkey === false &&
      needsFactorSetup(false, await readHoldsAuthenticator(stepUpHost))
    ) {
      setStep({ kind: 'offering', board });
      return;
    }
    await openBoard(board);
  };

  /**
   * The backend says this request carried no sign-in (`board_director_sign_in_required`: signed out
   * of every device, or the account is gone), though this tab still holds one. Back to the signed-out
   * invitation (contract §4.2), whose ways in start a fresh sign-in; picking one marks the join again.
   * Above them it says why (copy round 2, D-CBG-18): pressing Join and landing back at the ways in
   * with no word would leave the director to guess.
   */
  const signInEnded = () => {
    clearJoinIntent();
    markSignInEnded(token);
    setEndedWhileJoining(true);
    setStep({ kind: 'idle' });
    void host.signOut(here);
  };

  const claim = async (code?: string) => {
    if (code === undefined) setStep({ kind: 'joining' });
    else
      setStep((current) =>
        current.kind === 'code-step'
          ? { ...current, submitting: true, linkUnavailable: undefined }
          : current,
      );
    try {
      const { data } = await operations.directorClaimBoardSeat({
        body: code === undefined ? { invitation_token: token } : { invitation_token: token, code },
        throwOnError: true,
      });
      clearJoinIntent();
      await joined(decodeDirectorBoard(data));
    } catch (error) {
      switch (refusalCode(error)) {
        case DIRECTOR_REFUSALS.claimCodeRequired: {
          const context = isGivmoError(error) ? firstErrorContext(error) : undefined;
          setStep({
            kind: 'code-ask',
            masked: text(context?.masked_email_of_record) ?? masked,
            sending: false,
          });
          return;
        }
        case DIRECTOR_REFUSALS.claimCodeFailed:
          // Counted, never a flag: a flag already set can't announce a second wrong code, and the
          // boxes would keep it with nothing said (review-43 F1).
          setStep((current) =>
            current.kind === 'code-step'
              ? { ...current, failures: current.failures + 1, submitting: false }
              : { kind: 'code-step', masked, failures: 1, sentAt: 0, submitting: false },
          );
          return;
        case DIRECTOR_REFUSALS.alreadySeatedHere:
          setStep({ kind: 'already-seated' });
          return;
        case DIRECTOR_REFUSALS.anotherAccountRequired:
          clearJoinIntent();
          setStep({ kind: 'another-account' });
          return;
        case DIRECTOR_REFUSALS.invitationUnavailable:
          clearJoinIntent();
          setStep({ kind: 'unavailable' });
          return;
        case DIRECTOR_REFUSALS.signInRequired:
          signInEnded();
          return;
        case DIRECTOR_REFUSALS.seatLinkUnavailable: {
          // CBG-FIX161: the invitation opened, and nothing was linked or spent but one attempt of a
          // typed code, which stays good. So the step stays (the code in its boxes) and says
          // Board's sentence; pressing Join again sends the same code. "Try again" on the generic
          // failure would reset the page, and a new code would count against the seat's daily cap.
          const said = seatLinkUnavailableSentence(error, 'claim');
          setStep((current) =>
            current.kind === 'code-step'
              ? { ...current, submitting: false, linkUnavailable: said }
              : { kind: 'idle', linkUnavailable: said },
          );
          return;
        }
        default:
          setStep({ kind: 'failed' });
      }
    }
  };

  const requestCode = async () => {
    setStep((current) => (current.kind === 'code-ask' ? { ...current, sending: true } : current));
    try {
      const { data } = await operations.directorRequestBoardSeatClaimCode({
        body: { invitation_token: token },
        throwOnError: true,
      });
      const answer = decodeClaimCode(data);
      setStep(
        answer.emailSent
          ? {
              kind: 'code-step',
              masked: answer.masked ?? masked,
              failures: 0,
              sentAt: Date.now(),
              submitting: false,
            }
          : { kind: 'code-unsent', reason: 'skip', masked: answer.masked ?? masked },
      );
    } catch (error) {
      switch (refusalCode(error)) {
        case DIRECTOR_REFUSALS.claimCodeThrottled:
          // The cap is reached, but the newest code sent still joins: never a dead end without the
          // boxes to type it in. From the ask, the boxes open with no new code sent.
          setStep((current) =>
            current.kind === 'code-step'
              ? { ...current, throttled: true }
              : {
                  kind: 'code-step',
                  masked: current.kind === 'code-ask' ? current.masked : masked,
                  failures: 0,
                  sentAt: 0,
                  submitting: false,
                  throttled: true,
                },
          );
          return;
        case DIRECTOR_REFUSALS.claimCodeNotSent:
          setStep({ kind: 'code-unsent', reason: 'failed', masked });
          return;
        case DIRECTOR_REFUSALS.alreadySeatedHere:
          setStep({ kind: 'already-seated' });
          return;
        case DIRECTOR_REFUSALS.anotherAccountRequired:
          setStep({ kind: 'another-account' });
          return;
        case DIRECTOR_REFUSALS.invitationUnavailable:
          setStep({ kind: 'unavailable' });
          return;
        case DIRECTOR_REFUSALS.signInRequired:
          signInEnded();
          return;
        default:
          setStep({ kind: 'failed' });
      }
    }
  };

  // Signed in FROM this page: the visitor already chose to join (D-CBG-18 answer 3). Once.
  useEffect(() => {
    if (account === null || autoClaimed.current || preview === null) return;
    if (preview.claimed_by_you) {
      clearJoinIntent();
      return;
    }
    // CBG-SEC2-L4: this account can't join (frame a1); the claim would only refuse it.
    if (preview.another_account_required === true) {
      clearJoinIntent();
      return;
    }
    if (!takeJoinIntent(token)) return;
    autoClaimed.current = true;
    void claim();
    // `claim` closes over this render's preview; the guard keeps it to one call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, preview, token]);

  const useAnotherAccount = () => {
    clearJoinIntent();
    void host.signOut(here);
  };

  // CBG-SEC2-L4 (frame a1): sign the locked account out, and hint at the other account (a2).
  const useAnotherGivmoAccount = () => {
    clearJoinIntent();
    markAnotherAccount(token);
    setLookingForAnother(true);
    void host.signOut(here);
  };

  // CBG-SEC2-L4 (frames a0, a1, a2 → a2c): the console's create form for another account.
  // Creating it from the invitation is choosing to join (D-CBG-18 answer 3): once it's confirmed and
  // signed in, the invitation claims at once. Signed in (a1), the account is signed out first, back
  // to this page, and the page that comes back signed out opens the form (the effect below).
  const openCreateForm = () => {
    if (preview === null) return;
    void host.createAnotherAccount({
      returnTo: here,
      invitation: {
        organizationName: preview.organization_name,
        maskedEmailOfRecord: preview.masked_email_of_record,
      },
    });
  };
  const createNewAccount = () => {
    if (preview === null) return;
    markJoinIntent(token);
    if (account !== null) {
      markCreateAnotherAccount(token);
      void host.signOut(here);
      return;
    }
    openCreateForm();
  };
  useEffect(() => {
    if (account !== null || preview === null) return;
    if (takeCreateAnotherAccount(token)) openCreateForm();
    // `openCreateForm` closes over this render's preview; the marker keeps it to one call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, preview, token]);

  // CBG-SEC2-L4 (frame a1): the pointer names the organization that locks the account only where the
  // account's own security read does, so read it now through the step-up host, as Sign does before
  // its choice.
  const pointerShown =
    account !== null &&
    !(preview?.claimed_by_you ?? false) &&
    (preview?.another_account_required === true || step.kind === 'another-account');
  useEffect(() => {
    if (!pointerShown) return;
    void readHoldsAuthenticator(stepUpHost);
  }, [pointerShown, stepUpHost]);

  if (step.kind === 'offering') {
    // Joined: the header names the seat and the board it just joined, as frame `m1-joined` draws it.
    return (
      <DirectorSetupOfferPage
        variant="default"
        identity={directorBoardIdentity(step.board)}
        onSettled={() => void openBoard(step.board)}
      />
    );
  }

  const refusedOnPreview =
    previewQuery.isError &&
    refusalCode(previewQuery.error) === DIRECTOR_REFUSALS.invitationUnavailable;
  if (
    step.kind === 'unavailable' ||
    refusedOnPreview ||
    (previewQuery.isSuccess && preview === null)
  ) {
    return (
      <Unavailable
        signedIn={account !== null}
        onSwitch={useAnotherAccount}
        basePath={basePath}
        committees={invitationCommitteesOn(token)}
      />
    );
  }

  if (previewQuery.isPending) {
    // CBG-FIX106 (row 4.7): said before the invitation says whose seat it is.
    return (
      <DirectorShell title="Your invitation" testId="page-director-redeem">
        <Skeleton
          className="h-84 w-full max-w-[440px] rounded-lg"
          testId="director-invitation-loading"
        />
      </DirectorShell>
    );
  }

  if (previewQuery.isError || preview === null || step.kind === 'failed') {
    return (
      <DirectorShell title="Your invitation" testId="page-director-redeem">
        <Card className="w-full max-w-[440px]" testId="director-invitation-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className={BODY}>We couldn’t open this invitation just now.</p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => {
                setStep({ kind: 'idle' });
                void previewQuery.refetch();
              }}
              testId="director-invitation-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </DirectorShell>
    );
  }

  const organization = preview.organization_name ?? 'An organization';
  // CBG-YR6-OUTSIDE (frame o6): an invitation for someone who isn't on the board is to its committees.
  const committees = preview.committee_names;
  const toACommittee = committees.length > 0;

  if (preview.claimed_by_you && account !== null) {
    return (
      <DirectorShell
        title={
          toACommittee
            ? co.alreadyJoined(preview.organization_name, committees)
            : preview.organization_name === null
              ? 'You’ve already joined this board'
              : `You’ve already joined the ${preview.organization_name} board`
        }
        description={account.email === null ? undefined : `You joined with ${account.email}.`}
        testId="page-director-redeem"
      >
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          className="self-start"
          onClick={() => void openBoard(null)}
          testId="director-invitation-open"
        >
          {toACommittee ? co.openButton(committees) : 'Open your board'}
        </Button>
      </DirectorShell>
    );
  }

  return (
    <DirectorShell
      title={
        toACommittee
          ? co.invitedYou(organization, committees)
          : `${organization} invited you to its board`
      }
      description={`This invitation is for ${preview.seat_display_name}, sent to ${preview.masked_email_of_record}.`}
      testId="page-director-redeem"
    >
      {account === null && endedWhileJoining ? (
        <p
          role="status"
          className={`${BODY} w-full max-w-[440px]`}
          {...testIdProp('director-invitation-sign-in-ended')}
        >
          Your sign-in ended. Sign in again to join.
        </p>
      ) : null}

      {account === null && lookingForAnother ? (
        // Frame a2, above the ways in. Signed out: names no organization.
        <p
          className={`${BODY} w-full max-w-[440px]`}
          {...testIdProp('director-invitation-another-hint')}
        >
          Sign in with your other Givmo account: one your {host.lockingOrganizationNoun} doesn’t
          manage.
        </p>
      ) : null}

      {account === null
        ? host.renderWaysIn({
            // CBG-SEC2-L4 (a2): after "Use another Givmo account", the ways in leave out the
            // organization door, which would lead straight back to the account just left.
            arrival: lookingForAnother ? 'another_account' : 'invitation',
            returnTo: here,
            onChosen: () => {
              clearSignInEnded();
              setEndedWhileJoining(false);
              markJoinIntent(token);
            },
            invitation: {
              organizationName: preview.organization_name,
              maskedEmailOfRecord: preview.masked_email_of_record,
            },
            // CBG-SEC2-L4 (a0): Board's one line in Account Security's lock notice, from an invitation.
            lockNoticeLine: (
              <AnotherAccountNoticeLine
                organizationName={preview.organization_name}
                noun={host.lockingOrganizationNoun}
                onCreate={createNewAccount}
              />
            ),
          })
        : null}

      {account === null && lookingForAnother ? (
        <p
          className="w-full max-w-[440px] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('director-invitation-another-create')}
        >
          {/* Frame a2, under the ways in. */}
          Don’t have one?{' '}
          <button type="button" className={LINK} onClick={createNewAccount}>
            Create new account
          </button>
        </p>
      ) : null}

      {account !== null && pointerShown ? (
        <StepCard testId="director-invitation-another-account">
          {/* The account comes first, as on every signed-in step: it is the one that can't join. */}
          <p className={BODY}>
            You’re signed in as <span className="font-w600 text-text-strong">{account.email}</span>.{' '}
            {/* Frame a1, the locking organization named only where the account's own security
                read names it; "Your organization" otherwise. An invitation to a committee says
                the committee (CBG-FIX106, row 4.8). */}
            {toACommittee ? (
              co.lockedCantJoin(host.lockingOrganizationName ?? 'Your organization', committees)
            ) : (
              <>
                {host.lockingOrganizationName ?? 'Your organization'} manages how you sign in to
                this account, so it can’t join this board. Join with another Givmo account instead.
              </>
            )}
          </p>
          {/* The two ways on sit closer than the card's rows (frame a1). */}
          <div className="flex flex-col gap-12">
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              fullWidth
              onClick={createNewAccount}
              testId="director-invitation-create-account"
            >
              {/* Jonathan's words, 2026-10-07 19:13 PDT. */}
              Create new account
            </Button>
            <Button
              appearance="saas"
              tone="secondary"
              size="lg"
              fullWidth
              onClick={useAnotherGivmoAccount}
              testId="director-invitation-use-another-givmo-account"
            >
              {/* Jonathan's words, 2026-10-07 19:13 PDT. */}
              Use another Givmo account
            </Button>
          </div>
        </StepCard>
      ) : null}

      {account !== null && !pointerShown && (step.kind === 'idle' || step.kind === 'joining') ? (
        <StepCard testId="director-invitation-join">
          {/* The account comes BEFORE the act it binds: joining connects THIS account to the seat. */}
          <p className={BODY}>
            You’ll join as <span className="font-w600 text-text-strong">{account.email}</span>.
          </p>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={step.kind === 'joining'}
            onClick={() => void claim()}
            testId="director-invitation-join-button"
          >
            {toACommittee ? co.joinButton(committees) : 'Join the board'}
          </Button>
          {step.kind === 'idle' && step.linkUnavailable !== undefined ? (
            <ErrorMessage testId="director-invitation-link-unavailable">
              {step.linkUnavailable}
            </ErrorMessage>
          ) : null}
          <UseAnotherAccount onClick={useAnotherAccount} />
        </StepCard>
      ) : null}

      {account !== null && step.kind === 'code-ask' ? (
        <StepCard testId="director-invitation-code-ask">
          <p className={BODY}>
            You’re signed in as {account.email}, but this invitation went to {step.masked}. We’ll
            email a 6-digit code there to confirm the invitation is yours.
          </p>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={step.sending}
            onClick={() => void requestCode()}
            testId="director-invitation-send-code"
          >
            Email me a code
          </Button>
          <UseAnotherAccount onClick={useAnotherAccount} />
        </StepCard>
      ) : null}

      {account !== null && step.kind === 'code-step' ? (
        <CodeStep
          key={step.sentAt}
          masked={step.masked}
          failures={step.failures}
          sentAt={step.sentAt}
          submitting={step.submitting}
          throttled={step.throttled === true}
          action={toACommittee ? co.joinButton(committees) : 'Join the board'}
          testIdPrefix="director-invitation"
          notice={step.linkUnavailable ?? null}
          onSubmit={(code) => void claim(code)}
          onResend={() => void requestCode()}
          onUseAnotherAccount={useAnotherAccount}
        />
      ) : null}

      {account !== null && step.kind === 'code-unsent' ? (
        <StepCard testId="director-invitation-code-unsent">
          <p className={BODY}>
            {step.reason === 'failed'
              ? 'We couldn’t email a code just now. Try again in a moment.'
              : 'We can’t email a code from here. Ask whoever invited you to check the address on your seat.'}
          </p>
          {step.reason === 'failed' ? (
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              fullWidth
              onClick={() => void requestCode()}
              testId="director-invitation-send-code"
            >
              Email me a code
            </Button>
          ) : null}
          <UseAnotherAccount onClick={useAnotherAccount} />
        </StepCard>
      ) : null}

      {account !== null && step.kind === 'already-seated' ? (
        <StepCard testId="director-invitation-already-seated">
          <p className={BODY}>
            {/* CBG-FIX106 (row 4.9, approved 2026-10-17): the seat it holds may be a committee's,
                so the line names the organization, true of either. */}
            {account.email} already has a seat{' '}
            {preview.organization_name === null ? 'here' : `with ${preview.organization_name}`}. If
            this invitation is for someone else, they’ll need to open it with their own Givmo
            account.
          </p>
          <Button
            appearance="saas"
            tone="secondary"
            size="lg"
            fullWidth
            onClick={() => void openBoard(null)}
            testId="director-invitation-open"
          >
            {holdsOnlyCommitteeSeats(meQuery.data) ? co.openYourCommittees : 'Open your board'}
          </Button>
          <UseAnotherAccount onClick={useAnotherAccount} />
        </StepCard>
      ) : null}
    </DirectorShell>
  );
}

/**
 * CBG-SEC2-L4 (frame a0): Board's one line in Account Security's lock notice, when a way in on the
 * invitation meets a sign-in lock. Signed out, it names no locking organization: only the inviting
 * one, and the kind the console calls an organization that locks sign-in (`lockingOrganizationNoun`).
 */
function AnotherAccountNoticeLine({
  organizationName,
  noun,
  onCreate,
}: {
  organizationName: string | null;
  noun: string;
  onCreate: () => void;
}) {
  // "create a new one" opens the create form.
  return (
    <span {...testIdProp('director-invitation-lock-line')}>
      If {organizationName ?? 'this organization'} isn’t your {noun}, join its board with another
      Givmo account: sign in with it above, or{' '}
      <button type="button" className={LINK} onClick={onCreate}>
        create a new one
      </button>
      .
    </span>
  );
}

/** "Use another account" with the invitation's own test id. */
function UseAnotherAccount({ onClick }: { onClick: () => void }) {
  return (
    <UseAnotherAccountLink onClick={onClick} testId="director-invitation-use-another-account" />
  );
}

/**
 * The one refusal: one quiet page for every cause, naming no organization. `committees`: how many
 * committees this invitation's preview named in this tab, 0 for the board's, or null when this tab
 * never read it (CBG-FIX106, row 4.10).
 */
function Unavailable({
  signedIn,
  onSwitch,
  basePath,
  committees,
}: {
  signedIn: boolean;
  onSwitch: () => void;
  basePath: string;
  committees: number | null;
}) {
  return (
    <DirectorShell
      title="This invitation can’t be used anymore"
      description="Ask whoever sent it for a new one."
      testId="page-director-redeem"
    >
      <p
        className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp('director-invitation-unavailable-sign-in')}
      >
        {signedIn ? (
          <>
            Joined with a different account?{' '}
            <button type="button" className={LINK} onClick={onSwitch}>
              Switch account
            </button>
          </>
        ) : (
          <>
            Already joined?{' '}
            <Link
              to={BOARD_DIRECTOR_PAGES.signIn}
              search={{ redirect: boardPath(basePath, BOARD_DIRECTOR_PAGES.home) }}
              className={LINK}
            >
              Sign in
            </Link>
            {committees === null ? (
              // CBG-FIX111 (row 1.1, approved as built): a refused preview names nothing, so the
              // page can't know whose invitation it was, and says the line true of both.
              '.'
            ) : (
              <> {committees === 0 ? 'to open your board.' : co.signInToOpen(committees)}</>
            )}
          </>
        )}
      </p>
    </DirectorShell>
  );
}
